import { createRoute, useLoaderData } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { unwrapPageInitialData, type PageInitialData } from "~/app/api/pageData";
import { catchAllPageQueryOptions } from "~/app/api/pageData/catchAllPageQuery";
import type { DefaultPageInitialData } from "~/app/api/pageData/defaultPageInitialData";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { rootRoute } from "~/app/router/rootRoute";
import {
  extractProductRouteTarget,
  loadProductRoutePageData,
  ProductRouteContent,
  type ProductRouteLoaderData,
} from "~/app/router/routes/productRoute";

/**
 * Template UIDs that the catch-all route is safe to render via StorefrontShellFrame.
 *
 * The PageTemplateRenderer hard-fails on unregistered templateUids (line 26-29).
 * This allowlist is the gate: only templateUids known to be registered in
 * pageTemplateRegistry.ts may render through the React shell. Everything else
 * triggers a full-page reload to let the JSP route serve the page.
 *
 * This list reflects the audited catch-all rollout through tasks 0162-0165, plus the
 * not-found page (ErrorPageTemplate, task 0182 — body-only, components all transformer-covered).
 * ForkPageTemplate is not included because /static/welcome is the only page
 * using it and has its own concrete route (forkPageRoute.tsx).
 */
export const CATCH_ALL_SAFE_TEMPLATE_UIDS = [
  "GenericReactPageTemplate",
  "ContentPageST4Template",
  "CompanyPageTemplate",
  "StaticContentPageTemplate",
  "ContentPage1Template",
  "ProductGroupPageTemplate",
  "SimpleContentPageTemplate",
  "ContentStreamPageTemplate",
  "ErrorPageTemplate",
] as const;

export function isSafeTemplateUid(templateUid: string | undefined): boolean {
  return templateUid !== undefined && (CATCH_ALL_SAFE_TEMPLATE_UIDS as readonly string[]).includes(templateUid);
}

export type CatchAllRouteLoaderData =
  | ProductRouteLoaderData
  | { pageType: "generic"; result: PageInitialData<DefaultPageInitialData> };

function CatchAllRoute() {
  const page = useLoaderData({
    from: "/$",
  }) as CatchAllRouteLoaderData;

  if (page.pageType !== "generic") {
    return <ProductRouteContent page={page} />;
  }

  return <GenericCatchAllRouteContent result={page.result} />;
}

function GenericCatchAllRouteContent({ result }: { result: PageInitialData<DefaultPageInitialData> }) {
  const shellProps = useStorefrontShellProps();

  // Allowlist gate: if the backend reports a templateUid that is not registered
  // in pageTemplateRegistry.ts, a full-page reload hands the request back to
  // the JSP servlet which can still serve the page. This avoids the hard-fail
  // inside PageTemplateRenderer while keeping the user on the same URL.
  if (!isSafeTemplateUid(result.metadata.templateUid)) {
    window.location.replace(window.location.href);
    return null;
  }

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={result.metadata}>
      {null}
    </StorefrontShellFrame>
  );
}

function getLookupPath(splat: string | undefined): string {
  if (!splat) {
    return "";
  }
  return splat.startsWith("/") ? splat.slice(1) : splat;
}

export async function loadCatchAllPageData(
  context: { queryClient: QueryClient },
  splat: string | undefined,
  location: { pathname: string; searchStr: string } = { pathname: `/${splat ?? ""}`, searchStr: "" },
) {
  const lookupPath = getLookupPath(splat);
  const result = await context.queryClient.ensureQueryData(catchAllPageQueryOptions(lookupPath, location));

  return unwrapPageInitialData(result);
}

export async function loadCatchAllRoutePageData(
  context: { queryClient: QueryClient },
  splat: string | undefined,
  location: { pathname: string; searchStr: string } = { pathname: `/${splat ?? ""}`, searchStr: "" },
): Promise<CatchAllRouteLoaderData> {
  if (extractProductRouteTarget(splat)) {
    return loadProductRoutePageData(context, { _splat: splat }, location);
  }

  return {
    pageType: "generic",
    result: await loadCatchAllPageData(context, splat, location),
  };
}

export const catchAllRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/$",
  loader: async ({ context, params, location }) => loadCatchAllRoutePageData(context, params._splat, location),
  component: CatchAllRoute,
});
